import { MEMBER_NRT_SIDE_EFFECT, MEMBER_ORDER_STATUS } from '../orders/orders';
import { z } from 'zod';

// ─── NRT ordering vocabularies ───────────────────────────────────────────────
//
// The governed enums behind the NRT request + order contract. Each `as const`
// array is the single source for its wire schema here AND — with one deliberate
// exception called out below — the `text` + CHECK constraint on the matching
// column in the order data model (ADR-056 Rule 8). A migration that hardcodes a
// parallel list instead of importing from here is the drift this file exists to
// prevent.
//
// Nothing in this module imports from the rest of the NRT module: the request,
// response and route-contract layers all depend on these constants, so they
// must sit at the bottom.

/**
 * An order's lifecycle status. There is no stored `status` column — the
 * current status is derived from the latest status-event row — so this
 * vocabulary types the events and the derived value alike.
 *
 * `submitted` (handed to the fulfilment provider) is deliberately distinct
 * from `shipped` (the provider reports it left the warehouse): collapsing
 * them is what let a request sit unfulfilled while a member was told it was
 * on its way.
 */
/**
 * RE-EXPORTED, not re-declared. `packages/shared/src/orders/orders.ts` is the
 * single source for both of these vocabularies, and the direction is not
 * arbitrary: that module feeds the DB CHECK constraints
 * (`member_nrt_requests_side_effects_check` and
 * `member_order_status_events_status_check` in `db/schema.ts`), and the
 * database is the deeper constraint. Deriving the wire enum from it makes it
 * physically impossible for the contract to accept a value the write will
 * reject.
 *
 * This is the reconciliation `orders.ts` asked for: "when both children land
 * on the epic branch, one of the two must become the other's source. The
 * values are identical today and a drift test should replace this comment at
 * that point." Both children are now on the branch, so the trigger fired. Until now they were two independent literal arrays, and the failure
 * they permitted was the accepted-but-nothing-happened shape the whole
 * contract exists to forbid: widen the wire array alone and the route returns
 * 2xx, then the insert dies on a 23514 CHECK violation after the member has
 * been told their request was taken.
 */
export const NRT_ORDER_STATUS = MEMBER_ORDER_STATUS;
export const nrtOrderStatusSchema = z.enum(NRT_ORDER_STATUS);
export type NrtOrderStatus = z.infer<typeof nrtOrderStatusSchema>;

/**
 * The TERMINAL statuses — those from which nothing further ships. Their
 * complement is what "nothing further will ship from here" means, so the
 * set and its predicate are exported rather than re-derived at each call site,
 * giving that question exactly one definition.
 */
export const NRT_ORDER_STATUS_TERMINAL = ['rejected', 'delivered', 'cancelled'] as const;

// Widened to `string` by annotation rather than assertion: `.includes` on the
// narrow tuple would reject any status outside it at compile time, which is the
// opposite of what a predicate over the full vocabulary needs (ADR-117 — no
// type assertions at a boundary; the lint enforces it).
const TERMINAL_ORDER_STATUSES: ReadonlySet<string> = new Set(NRT_ORDER_STATUS_TERMINAL);

/** True when the order has reached a state from which nothing further ships. */
export function isTerminalOrderStatus(status: NrtOrderStatus): boolean {
  return TERMINAL_ORDER_STATUSES.has(status);
}

/**
 * The statuses on which no care team member has decided yet.
 *
 * A DIFFERENT QUESTION FROM THE TERMINAL SET, over the same vocabulary. The
 * terminal set answers "can anything still ship from here", and the member's
 * current-request read is built on it. This one answers "is a care team member
 * still looking at this", and the create gate is built on it. They are not
 * complements and must not be collapsed: an `approved` order is neither
 * terminal nor awaiting approval, and that gap is exactly the state in which a
 * member may ask for more.
 *
 * Why a set of one rather than `status === 'created'`: the two behave
 * identically until a status joins the vocabulary, at which point an inequality
 * silently admits it and a declared set makes the omission visible here, at the
 * one place the sets live. `order-transitions.ts` makes the same argument about
 * its own from-states, and `statusesWithNoLegalAction()` already cross-checks
 * that file's sets against the terminal set.
 */
export const NRT_ORDER_STATUS_AWAITING_APPROVAL = ['created'] as const;

// Widened to `string` for the same reason as `TERMINAL_ORDER_STATUSES` above.
const AWAITING_APPROVAL_ORDER_STATUSES: ReadonlySet<string> = new Set(
  NRT_ORDER_STATUS_AWAITING_APPROVAL,
);

/**
 * True when the order is still waiting on a care team member's decision.
 *
 * Takes `null` because the caller's input is a DERIVED status — the newest
 * `member_order_status_events` row for the request's order — and a request with
 * no order row at all is a legal shape the legacy import produces. Null is not
 * awaiting approval: there is no order for anyone to approve, so there is
 * nothing for the gate to protect. Accepting null here rather than making every
 * call site null-check keeps that decision in one place instead of two.
 */
export function isAwaitingApprovalOrderStatus(status: NrtOrderStatus | null): boolean {
  return status !== null && AWAITING_APPROVAL_ORDER_STATUSES.has(status);
}

/**
 * The `source_ref` suffix of a request row the 1.0 import made up rather than
 * copied.
 *
 * 1.0 created a member's starter shipment directly, with no request pointing at
 * it. The create gate only sees an order through a request row, so the import
 * writes one for such an order while it is still open — a row the member never
 * asked for. The writer and the member-facing reads that must leave it out
 * share this one value, so they cannot disagree about which rows it names.
 */
export const NRT_SYNTHETIC_REQUEST_SUFFIX = ':synthetic-request';

/**
 * What the member reported experiencing. **Closed on the wire, open in the
 * column** — the one deliberate departure from ADR-056 Rule 8 in this
 * contract, and the reason this comment is long.
 *
 * The vocabulary is `none` / `other` and nothing else. A richer picklist
 * exists in neither codebase, and inventing one here would silently discard
 * whatever the member actually experienced at the boundary — the same shape
 * as the failures this contract exists to remove, just relocated into a
 * dropdown. What the member reports travels in `otherDetail` instead, which
 * is why selecting `other` without it is a validation error rather than an
 * accepted empty answer.
 *
 * The matching `side_effect` column stays open `text` with NO CHECK. Do not
 * "tighten" it to match this enum: that would owe a migration every time a
 * clinical term is added, and would leave nowhere for the free-text detail to
 * live. Adding a member here later is additive on a REQUEST schema, so it
 * needs no migration and no endpoint version — an older client simply never
 * sends the new value.
 */
export const NRT_SIDE_EFFECT = MEMBER_NRT_SIDE_EFFECT;
export const nrtSideEffectSchema = z.enum(NRT_SIDE_EFFECT);
export type NrtSideEffect = z.infer<typeof nrtSideEffectSchema>;

/** Upper bound on the free-text side-effect detail. PHI — never logged. */
export const NRT_OTHER_DETAIL_MAX = 500;

/**
 * The states the member-facing supply read can be in. Five, not four: a
 * `rejected` or `cancelled` request that rendered as `none` would leave the
 * member with no way to learn their request was refused, which is precisely
 * the silent path the contract's invariant forbids.
 */
export const NRT_SUPPLY_STATE = ['none', 'pending', 'on_the_way', 'delivered', 'declined'] as const;
export const nrtSupplyStateSchema = z.enum(NRT_SUPPLY_STATE);
export type NrtSupplyState = z.infer<typeof nrtSupplyStateSchema>;

/**
 * Why the member cannot place a request right now, surfaced on the `none`
 * state of the supply read.
 *
 * Every value here is also an error code the create route can return. That
 * pairing is enforced BY CONSTRUCTION in `errors.ts` — the pre-condition codes
 * are type-pinned to this union in both directions, so a member-facing refusal
 * the read cannot express, or a read state the write cannot produce, fails the
 * build. It is what stops the app rendering an enabled action whose write is
 * guaranteed to refuse.
 */
export const NRT_MEMBER_BLOCK_REASON = [
  'no_shipping_address',
  'no_verified_name',
  'request_awaiting_approval',
  'not_enrolled_in_programme',
  'unsupported_shipping_country',
  'nrt_not_entitled',
  'previous_orders_pending',
  'migration_import_incomplete',
] as const;
export const nrtMemberBlockReasonSchema = z.enum(NRT_MEMBER_BLOCK_REASON);
export type NrtMemberBlockReason = z.infer<typeof nrtMemberBlockReasonSchema>;

/**
 * What a client reads when the server names a block reason this bundle does not
 * know.
 *
 * Deliberately NOT a member of `NRT_MEMBER_BLOCK_REASON`. That vocabulary is
 * type-pinned in both directions to the create route's pre-condition error
 * codes (see the docblock above), so adding a sentinel to it would assert that
 * the write can refuse with `unrecognised` — which it cannot. The tolerance is
 * a READ-side widening only; the emitting side keeps the closed enum.
 */
export const NRT_MEMBER_BLOCK_REASON_UNRECOGNISED = 'unrecognised' as const;

/**
 * The block reason as a CLIENT parses it — ADR-135 Rule 3.
 *
 * Rule 3 names "introducing an unhandled enum value" a breaking response
 * change, and this is the field that would break: the server adding a sixth
 * block reason would make every installed bundle fail the whole supply read on
 * `invalid_value`, losing the member their entire NRT surface rather than one
 * label. The repo's tolerant reader (`apps/mobile/services/tolerant-parse.ts`)
 * does not cover this — by design it only strips unknown KEYS, and treats an
 * unknown enum value as the real error it usually is — so the tolerance has to
 * live in the schema.
 *
 * FAIL-CLOSED, and it is structural rather than a convention a screen has to
 * remember. An unrecognised reason maps to a non-null sentinel, and the `none`
 * state's refinement forbids `canRequest: true` alongside any non-null reason —
 * so an unknown reason can only ever arrive with `canRequest: false`, which is
 * the flag the entry point is gated on. Mapping the unknown to `null` instead
 * would have been the dangerous shape twice over: it would read as "nothing is
 * blocking you", and it would fail the same refinement from the other side
 * (`canRequest: false` requires a reason), turning a tolerable response into a
 * hard parse error.
 *
 * The member still gets a sentence: `blockedMessage` is server-authored
 * (ADR-017) and arrives whatever the reason is called, so a client that cannot
 * name the reason can still explain it. That is why the sentinel needs no copy
 * of its own.
 */
export const nrtMemberBlockReasonReadSchema = z.union([
  nrtMemberBlockReasonSchema,
  // Reached only when the enum branch fails. `.min(1)` keeps a genuinely
  // malformed payload (`''`, a number, an object) failing rather than being
  // laundered into the sentinel — tolerance for a value we do not recognise,
  // not for a field that is the wrong shape.
  z
    .string()
    .min(1)
    .transform(() => NRT_MEMBER_BLOCK_REASON_UNRECOGNISED),
]);
export type NrtMemberBlockReasonRead =
  | NrtMemberBlockReason
  | typeof NRT_MEMBER_BLOCK_REASON_UNRECOGNISED;

/** Sort columns the staff request list accepts. */
export const NRT_REQUEST_LIST_SORT = ['requestedAt'] as const;
export const nrtRequestListSortSchema = z.enum(NRT_REQUEST_LIST_SORT);
export type NrtRequestListSort = z.infer<typeof nrtRequestListSortSchema>;

/** Sort directions the staff request list accepts. */
export const NRT_REQUEST_LIST_ORDER = ['asc', 'desc'] as const;
export const nrtRequestListOrderSchema = z.enum(NRT_REQUEST_LIST_ORDER);
export type NrtRequestListOrder = z.infer<typeof nrtRequestListOrderSchema>;

/**
 * The parsed status filter: an array of order statuses. `status` on the wire is
 * an opaque CSV string (the `memberListQuerySchema` convention — a query param
 * is a string, and the query schema says so); the server splits it and
 * validates each value against this schema, which is the single source of
 * truth for the allowed filter values.
 *
 * Published rather than left in the route's `.describe()` text: without it the
 * closed vocabulary exists only as prose, and `?status=approved,,created` or a
 * misspelled value reaches the handler with nothing to reject it — returning
 * the default page, which staff read as "there are no approved requests".
 */
export const nrtRequestListStatusFilterSchema = z.array(z.enum(NRT_ORDER_STATUS)).min(1);
export type NrtRequestListStatusFilter = z.infer<typeof nrtRequestListStatusFilterSchema>;

/** Default and maximum page sizes for the staff request list. */
export const NRT_REQUEST_LIST_DEFAULT_LIMIT = 25;
export const NRT_REQUEST_LIST_MAX_LIMIT = 100;

/**
 * The SHAPE an NRT sku must take: `NRT-{FORM}-{DOSE}`.
 *
 * Shape only. Whether a well-formed sku is a product anyone ships is a separate
 * question, answered by the catalogue (`../orders/catalogue`, with the NRT view
 * in `./catalogue`): `NRT-GUM-999MG` matches this pattern and is not stocked.
 * The two stay separate on purpose — this guard is the wire contract's
 * validator for existing routes, and tightening it to catalogue membership
 * would change what those routes accept.
 *
 * What it closes: the contract publishes `unknown_sku`, and a format guard
 * refuses `PATCH-DOES-NOT-EXIST` and every other free-text value, which is the
 * failure reachable from the staff UI.
 */
export const NRT_SKU_PATTERN = /^NRT-[A-Z]{2,12}-[0-9]{1,3}MG$/;

/** Does this sku take the published shape? */
export function isWellFormedNrtSku(sku: string): boolean {
  return NRT_SKU_PATTERN.test(sku);
}
